/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { fetchIntervention, findManyInterventions } from '../../lib/rest/interventionAPIClient'
import LabelledValue from '../LabelledValue'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { getInterventionCreateLink, getInterventionLink } from '../../lib/links/interventionLinks'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { ActionIcon, Box, Group, Text } from '@mantine/core'
import InterventionStatusBadge from '../intervention/InterventionStatusBadge'

export function getInterventionOptionLabel (item: Intervention) {
  return `${item.subject}`
}

export function InterventionOption (props: SearchInputRenderOption<Intervention>) {
  const { option } = props
  const { item } = option
  const intl = useIntl()
  return (
    <Box w="100%">
      <Group align="start">
        <Text inherit>
          {getInterventionOptionLabel(item)}
        </Text>
        <InterventionStatusBadge
          ml="auto"
          intervention={item}
          size="sm"
        />
      </Group>
      <Box fz="sm" c="dimmed">
        <LabelledValue
          label={intl.formatMessage({
            id: 'request_date',
            defaultMessage: 'Date de la demande'
          })}
          value={item.requestedAt ? intl.formatDate(item.requestedAt) : '?'}
        />
        <LabelledValue
          label={intl.formatMessage({
            id: 'intervention_date',
            defaultMessage: 'Date d\'intervention'
          })}
          value={item.startDate ? intl.formatDate(item.startDate) : '?'}
        />
        <LabelledValue
          label={intl.formatMessage({
            id: 'assignee',
            defaultMessage: 'Responsable'
          })}
          value={item.assignee}
        />
        {item.Estate != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'estate',
              defaultMessage: 'Bien'
            })}
            value={(
              <>
                {item.Estate.reference}
                -
                {item.Estate.title}
              </>
            )}
          />
          : null}
      </Box>
    </Box>
  )
}

export type InterventionInputProps = Omit<SearchInputProps<Intervention>, 'data'> & {
  queryFilter?: Record<string, any>,
}

function InterventionInput (props: InterventionInputProps) {
  const intl = useIntl()
  const {
    disabled,
    id,
    label,
    name = 'interventionId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['interventions', queryFilter, searchParams],
    queryFn: () => findManyInterventions({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.interventions : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<Intervention>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getInterventionOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const interventionQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['intervention', value],
    queryFn: () => fetchIntervention(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: intervention } = interventionQuery

  useEffect(() => {
    if (intervention != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getInterventionOptionLabel(intervention)
      }))
    }
  }, [intervention, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'intervention',
        defaultMessage: 'Intervention'
      })}
      {...others}
      data={options}
      disabled={disabled}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={InterventionOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
      rightSection={<>
        {value == null
          ? <ActionIcon
            component={AnchorLink}
            target="_blank"
            title={intl.formatMessage({
              id: 'new',
              defaultMessage: 'Nouveau'
            })}
            to={getInterventionCreateLink()}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faPlusCircle}
            />
          </ActionIcon>
          : <ActionIcon
            disabled={!value}
            component={AnchorLink}
            target="_blank"
            to={getInterventionLink(Number(value))}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faLink}
            />
          </ActionIcon>
        }

      </>}
    />
  )
}

export default InterventionInput
